import type { ComponentChildren } from 'preact'
import { useState, useEffect } from 'preact/hooks'
import { X, Loader2, CheckCircle, Unplug, AlertTriangle, Info } from 'lucide-preact'
import { TextBox } from '@/components'
import { useDynamicsAuth } from '@/hooks/useDynamicsAuth'
import { IntegrationStatusBadge, IntegrationErrorDetail } from '../components/health'
import { ScopeList } from '../components/ScopeList'
import { CrmSlotTakenNotice } from '../components/CrmSlotTakenNotice'
import { crmBlockedDetail, crmBlockedHeadline } from '../crmSlot'
import { IntegrationProvider } from '@/types/integrations'

interface DynamicsConnectPanelProps {
    isOpen: boolean
    /** The CRM already holding the company's slot, when there is one. */
    blockedByCrm?: string | null
    clientCompanyId: string
    onClose: () => void
    onComplete: () => void
}

/**
 * Slide-in panel for the Microsoft Dynamics 365 OAuth connection. Dynamics is OAuth-based (Microsoft
 * Entra ID, auth-code + PKCE) like HubSpot/Salesforce, so it gets a dedicated connect/status panel
 * rather than the generic API-key IntegrationPanel. Unlike Salesforce, the customer must supply their
 * Dataverse environment URL before connecting — the Entra resource scope is per-org. v1 is connect /
 * status / disconnect only — no field-mapping wizard yet (that lands with the contact-field sync work).
 *
 * It also carries the one provider-side prerequisite the sync cannot work around: "Track changes" on
 * the Opportunity table. Most environments already have it, so it is stated before the connect form as
 * a heads-up rather than a task — and reported firmly in the two places it has actually gone wrong:
 * when the API refuses a connect over it, and on an existing connection whose org does not have it
 * (ORB-621).
 */
export function DynamicsConnectPanel({ isOpen, blockedByCrm, clientCompanyId, onClose, onComplete }: DynamicsConnectPanelProps) {
    const {
        isConnected, connectionId, status, scopes, environmentUrl, accessTokenExpiresUtc,
        isSoftFailure, consecutiveFailureCount, recentErrors, aiSummary,
        connectedAtUtc, lastOutboundSyncUtc, lastInboundSyncUtc,
        isOpportunityChangeTrackingOff,
        isLoading, isConnecting, connectError, connect, disconnect,
    } = useDynamicsAuth(clientCompanyId, isOpen)

    // Holds an on-demand-generated AI summary so reopening within the same session reuses it.
    const [aiSummaryOverride, setAiSummaryOverride] = useState<string | null>(null)
    useEffect(() => {
        if (!isOpen) setAiSummaryOverride(null)
    }, [isOpen])

    if (!isOpen) return null

    const assetBase = (import.meta as ImportMeta).env?.VITE_ASSETS_BASE_URL?.replace(/\/$/, '') || ''
    const blockedBy = !isConnected && blockedByCrm ? blockedByCrm : null
    const headerCaption = isLoading
        ? 'Loading…'
        : isConnected ? 'Connection overview'
        : blockedBy ? 'Unavailable'
        : 'Connect your CRM'

    const handleDisconnect = async () => {
        await disconnect()
        onComplete()
    }

    return (
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm animate-fade-in" onClick={onClose} />

            {/* Panel */}
            <div className="fixed inset-y-0 right-0 z-50 w-full md:w-1/2 bg-white dark:bg-slate-900 shadow-2xl flex flex-col animate-slide-in">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-3">
                        <img src={`${assetBase}/assets/integrations/dynamics-logo.svg`} alt="Dynamics 365" className="w-8 h-8" />
                        <div>
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Dynamics 365 Integration</h2>
                            <p className="text-xs text-slate-500 dark:text-slate-400">{headerCaption}</p>
                        </div>
                    </div>
                    <button type="button" aria-label="Close" onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-6">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-full">
                            <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
                        </div>
                    ) : isConnected ? (
                        <div className="space-y-6">
                            {isOpportunityChangeTrackingOff && <ChangeTrackingWarning />}
                            <ConnectionStatusCard
                                environmentUrl={environmentUrl}
                                accessTokenExpiresUtc={accessTokenExpiresUtc}
                                scopes={scopes}
                                connectedAtUtc={connectedAtUtc}
                                lastOutboundSyncUtc={lastOutboundSyncUtc}
                                lastInboundSyncUtc={lastInboundSyncUtc}
                                healthStatus={status}
                                isSoftFailure={isSoftFailure}
                            />
                            {recentErrors.length > 0 && (
                                <IntegrationErrorDetail
                                    status={status}
                                    isSoftFailure={isSoftFailure}
                                    consecutiveFailureCount={consecutiveFailureCount}
                                    recentErrors={recentErrors}
                                    aiSummary={aiSummaryOverride ?? aiSummary}
                                    clientCompanyId={clientCompanyId}
                                    connectionId={connectionId}
                                    onSummaryFetched={setAiSummaryOverride}
                                />
                            )}
                        </div>
                    ) : (
                        blockedBy ? (
                            <CrmSlotTakenNotice
                                headline={crmBlockedHeadline(blockedBy)}
                                detail={crmBlockedDetail(blockedBy, IntegrationProvider.Dynamics)}
                            />
                        ) : (
                            <ConnectView isConnecting={isConnecting} connectError={connectError} onConnect={connect} assetBase={assetBase} />
                        )
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-slate-700">
                    <div>
                        {!isLoading && isConnected && (
                            <button
                                type="button"
                                onClick={handleDisconnect}
                                className="px-4 py-2 text-sm font-medium rounded-lg border border-red-300 dark:border-red-600 bg-white dark:bg-slate-800 text-red-600 dark:text-red-400 flex items-center gap-2 hover:bg-red-50 dark:hover:bg-red-900/30"
                            >
                                <Unplug className="w-4 h-4" /> Disconnect Dynamics 365
                            </button>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600"
                    >
                        Close
                    </button>
                </div>
            </div>
        </>
    )
}

// A Dataverse environment URL looks like https://<org>.crm[.region].dynamics.com. We validate lightly
// on the client for fast feedback; InitiateDynamicsOAuth is the authority (it normalizes + rejects).
function isLikelyEnvironmentUrl(value: string): boolean {
    try {
        const url = new URL(value.trim())
        return url.protocol === 'https:' && url.hostname.toLowerCase().endsWith('.dynamics.com')
    } catch {
        return false
    }
}

// The maker-portal path to the Track changes toggle, rendered wherever the setting is named so the
// wording and the trail to it never drift apart.
function TrackChangesPath() {
    return (
        <>
            <a
                href="https://make.powerapps.com"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 font-medium"
            >
                make.powerapps.com
            </a>
            {' → Tables → Opportunity → Advanced options → '}
            <span className="font-medium">Track changes</span>
        </>
    )
}

function ConnectView({ isConnecting, connectError, onConnect, assetBase }: { isConnecting: boolean; connectError: string | null; onConnect: (environmentUrl: string) => void; assetBase: string }) {
    const [environmentUrl, setEnvironmentUrl] = useState('')
    const trimmed = environmentUrl.trim()
    const isValid = isLikelyEnvironmentUrl(trimmed)
    const showHint = trimmed.length > 0 && !isValid

    return (
        <div className="space-y-8">
            <div className="text-center space-y-3">
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Connect to Dynamics 365</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                    Authorize OrbitalX to access your Microsoft Dynamics 365 (Dataverse) environment. This creates a
                    secure OAuth connection at the company level.
                </p>
            </div>

            {/*
              * A heads-up, not a task. Dynamics 365 environments provisioned with the Sales app
              * generally already have change tracking on for the Opportunity table, so telling every
              * customer to go and enable it sends most of them to raise an admin request for work that
              * is already done. It is still worth stating before the form: when it IS off the connect
              * is refused, and only a Dataverse administrator can fix that (ORB-621).
              */}
            <div className="max-w-md mx-auto rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 text-xs text-blue-800 dark:text-blue-300 space-y-2">
                <p className="flex items-center gap-2 font-semibold">
                    <Info className="w-4 h-4 shrink-0" />
                    Before you connect
                </p>
                <p>
                    We need <span className="font-medium">Change tracking</span> on the Opportunity table — it is what
                    lets us read only what changed instead of your whole CRM every 15 minutes, and how we learn about
                    deleted opportunities. Most Dynamics 365 environments already have it on, so you probably do not
                    need to do anything.
                </p>
                <p>
                    If it is off, the connect is refused and this panel will say so. A Dataverse administrator can turn
                    it on at <TrackChangesPath />. It is one-time and non-destructive — note that Microsoft does not
                    allow it to be turned off again once enabled.
                </p>
            </div>

            {connectError && (
                <div
                    role="alert"
                    className="max-w-md mx-auto flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 text-xs text-red-700 dark:text-red-300"
                >
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <p>{connectError}</p>
                </div>
            )}

            <div className="max-w-md mx-auto">
                <TextBox
                    name="dynamics-environment-url"
                    type="url"
                    label="Dataverse environment URL"
                    placeholder="https://your-org.crm.dynamics.com"
                    value={environmentUrl}
                    onChange={setEnvironmentUrl}
                    disabled={isConnecting}
                    error={showHint ? 'Enter the full https URL of your environment, e.g. https://your-org.crm.dynamics.com' : undefined}
                    helperText="Find this in Power Platform admin center under your environment’s details."
                />
            </div>

            <div className="flex justify-center">
                <button
                    onClick={() => onConnect(trimmed)}
                    disabled={isConnecting || !isValid}
                    className="inline-flex items-center gap-3 px-6 py-4 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-medium text-sm shadow-lg hover:shadow-xl transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isConnecting
                        ? <Loader2 className="w-5 h-5 animate-spin" />
                        : <img src={`${assetBase}/assets/integrations/dynamics-logo.svg`} alt="" className="w-6 h-6" />}
                    {isConnecting ? 'Connecting…' : 'Connect Dynamics 365'}
                </button>
            </div>
        </div>
    )
}

// Shown against a connection that already exists — one made before the connect-time check, or an org
// that regressed. The sync still runs, so this describes what is degraded rather than what is broken.
function ChangeTrackingWarning() {
    return (
        <div
            role="alert"
            className="flex items-start gap-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4 text-xs text-amber-800 dark:text-amber-300"
        >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="space-y-2">
                <p className="font-semibold">Change tracking is off on the Opportunity table</p>
                <p>
                    Every sync re-reads your full Opportunity table, and deleted opportunities are not detected — so
                    they keep counting toward pipeline totals. A Dataverse administrator can enable it:{' '}
                    <TrackChangesPath />.
                </p>
            </div>
        </div>
    )
}

function ConnectionStatusCard({ environmentUrl, accessTokenExpiresUtc, scopes, connectedAtUtc, lastOutboundSyncUtc, lastInboundSyncUtc, healthStatus, isSoftFailure }: {
    environmentUrl: string | null
    accessTokenExpiresUtc: string | null
    scopes: string
    connectedAtUtc: string | null
    lastOutboundSyncUtc: string | null
    lastInboundSyncUtc: string | null
    healthStatus: string
    isSoftFailure: boolean
}) {
    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-5 space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <div>
                        <h4 className="font-medium text-sm text-slate-900 dark:text-white">Connection Status</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Dynamics 365 is connected and active</p>
                    </div>
                </div>
                <span className="text-[10px] px-2.5 py-1 rounded-md font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">Connected</span>
            </div>

            <ul className="space-y-2 text-xs border-t border-slate-100 dark:border-slate-800 pt-4">
                {environmentUrl && <SummaryRow label="Environment" value={environmentUrl} />}
                <SummaryRow label="Connected" value={formatDate(connectedAtUtc)} />
                {accessTokenExpiresUtc && <SummaryRow label="Token expires" value={formatExpiry(accessTokenExpiresUtc)} />}
                {scopes && <SummaryRow label="Scopes" value={<ScopeList scopes={scopes} />} />}
                <SummaryRow label="Last outbound sync" value={formatSync(lastOutboundSyncUtc)} />
                <SummaryRow label="Last inbound sync" value={formatSync(lastInboundSyncUtc)} />
                {healthStatus && (
                    <SummaryRow
                        label="Sync health"
                        value={<IntegrationStatusBadge status={healthStatus} isSoftFailure={isSoftFailure} />}
                    />
                )}
            </ul>
        </div>
    )
}

function SummaryRow({ label, value }: { label: string; value: ComponentChildren }) {
    return (
        <li className="flex items-start justify-between gap-3">
            <span className="text-slate-500 dark:text-slate-400">{label}</span>
            <span className="text-slate-800 dark:text-slate-200 font-medium text-right break-all">{value}</span>
        </li>
    )
}

// Date only (connected date). Falls back to an em dash when absent/unparseable.
function formatDate(iso?: string | null): string {
    if (!iso) return '—'
    const d = new Date(iso)
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString()
}

// Date + time for the access-token expiry. Deliberately not formatSync: "Never" is the right word for
// a sync that has not run, but on an expiry it reads as "this token never expires" — the opposite of
// what an unparseable timestamp means. A dash says "unknown" without asserting anything.
function formatExpiry(iso?: string | null): string {
    if (!iso) return '—'
    const d = new Date(iso)
    return isNaN(d.getTime()) ? '—' : d.toLocaleString()
}

// Date + time for sync timestamps. "Never" reads better than a dash for a sync that hasn't run.
function formatSync(iso?: string | null): string {
    if (!iso) return 'Never'
    const d = new Date(iso)
    return isNaN(d.getTime()) ? 'Never' : d.toLocaleString()
}
